import { AuthStates, useAuthState, useLogout } from '@vis-auth/react';
import { EverifyUserRole, User } from '@vis-auth/vis-user-client';
import React, { FC, createContext, useContext, useEffect, useState } from 'react';

import { EveRoutes } from '@ver-uds/e-verify';
import Environment from '../../constants/Environment';
import { useEnvironment } from '../Environment/EnvironmentContext';
import useUserApi from '../../services/UserApi';

const UserContext = createContext<User | null>(null);

export const useUser = (): User | null => {
  const user = useContext(UserContext);

  if (!user) {
    throw new Error('useUser must be used inside UserProvider');
  }

  return user;
};

export const UserProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState(false);

  const authState = useAuthState();
  const userApi = useUserApi();
  const { ENVIRONMENT_NAME, MOCK_USER, MOCK_USER_ID } = useEnvironment();
  const logout = useLogout();

  if (authState.state !== AuthStates.authenticated) {
    throw new Error('UserProvider must be used inside SecureRoute');
  }

  let id = authState?.user?.user_id as string;

  if (ENVIRONMENT_NAME === Environment.LOCAL || ENVIRONMENT_NAME === Environment.DEVTEST) {
    if (MOCK_USER && MOCK_USER_ID) {
      id = MOCK_USER_ID;
    }
  }

  useEffect(() => {
    const fetchUser = async (): Promise<void> => {
      try {
        const response = await userApi.getUser({ id });

        // need to look at enrollment
        if (response.roles.includes(EverifyUserRole.ENROLLMENT)) {
          window.location.assign(EveRoutes.ACCOUNT_ENROLLMENT);
        } else {
          setUser(response);
        }
      } catch (e: any) {
        if (e.status === 401) {
          logout();
        } else {
          setError(true);
        }
      }
    };

    fetchUser();
  }, [authState.state, userApi, id, logout]);

  if (error) {
    throw new Error('Could not fetch user information');
  }

  if (user) {
    return (
      <UserContext.Provider value={user} data-testid="user-context-provider">
        {children}
      </UserContext.Provider>
    );
  }

  return <div data-testid="user-context-provider">Loading...</div>;
};
